import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme
} from '@mui/material';
import { CheckCircle, Close } from '@mui/icons-material';

// Styled Dialog for consistent styling
const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)',
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95), rgba(42, 42, 42, 0.95))'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(240, 245, 255, 0.95))',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        margin: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
      }
    }
  };
});

const DialogHeader = styled(DialogTitle)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
});

const SuccessButton = styled(Button)(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    background: isDarkMode 
      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '10px 20px',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
    '&:hover': {
      background: isDarkMode 
        ? 'linear-gradient(135deg, #047857 0%, #065f46 100%)'
        : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)',
    }
  };
});

/**
 * A reusable success dialog component
 */
const SuccessDialog = ({ 
  open, 
  onClose, 
  message, 
  title, 
  buttonText 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Use provided values or default to translations
  const dialogTitle = title || t('user.success.title');
  const dialogButtonText = buttonText || t('user.success.continue');

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle fontSize="small" />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem', color: 'white' }}>
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogHeader>
      
      <DialogContent sx={{ 
        pt: 4, 
        pb: 3,
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 2
        }}>
          <Box sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: isDarkMode 
              ? 'rgba(16, 185, 129, 0.2)' 
              : 'rgba(16, 185, 129, 0.1)',
            borderRadius: '50%',
          }}>
            <CheckCircle sx={{ fontSize: 60, color: '#10b981' }} />
          </Box>
          <Typography 
            variant="h6" 
            gutterBottom 
            align="center" 
            sx={{ 
              fontWeight: 600, 
              color: '#10b981' 
            }}
          >
            {dialogTitle}
          </Typography>
          <Typography 
            variant="body1" 
            align="center"
            sx={{ 
              color: isDarkMode ? '#ffffff' : 'inherit'
            }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 0,
        backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        borderTop: `1px solid ${isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'}`
      }}>
        <SuccessButton onClick={onClose} fullWidth>
          {dialogButtonText}
        </SuccessButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default SuccessDialog;